
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface UserSelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function UserSelectField({ value, onChange, placeholder = "Selecionar usuário" }: UserSelectFieldProps) {
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["company_users"],
    queryFn: async () => {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (!currentUser) return [];

      // Buscar usuários da mesma empresa através dos colaboradores
      const { data: collaborators, error: collabError } = await supabase
        .from("collaborators")
        .select("collaborator_id")
        .eq("company_id", currentUser.id);

      if (collabError) {
        console.error("Erro ao buscar colaboradores:", collabError);
        return [];
      }

      // Incluir o próprio usuário atual
      const userIds = [currentUser.id, ...(collaborators?.map(c => c.collaborator_id) || [])];

      // Buscar dados dos usuários
      const { data: usersData, error: usersError } = await supabase
        .from("users")
        .select("id, name, email, avatar_url")
        .in("id", userIds);

      if (usersError) {
        console.error("Erro ao buscar usuários:", usersError);
        return [];
      }

      return usersData || [];
    }
  });

  const handleValueChange = (newValue: string) => {
    // Convert "unassigned" back to empty string for the parent component
    onChange(newValue === "unassigned" ? "" : newValue);
  };

  if (isLoading) {
    return (
      <Select disabled>
        <SelectTrigger>
          <SelectValue placeholder="Carregando..." />
        </SelectTrigger>
      </Select>
    );
  }

  // Convert empty value to "unassigned" for the Select component
  const selectValue = value === "" ? "unassigned" : value;

  return (
    <Select value={selectValue} onValueChange={handleValueChange}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="unassigned">
          <span className="text-muted-foreground">Nenhum responsável</span>
        </SelectItem>
        {users.map((user) => (
          <SelectItem key={user.id} value={user.email || user.id}>
            <div className="flex items-center gap-2">
              {user.avatar_url ? (
                <img 
                  src={user.avatar_url} 
                  alt={user.name || user.email} 
                  className="w-4 h-4 rounded-full"
                />
              ) : (
                <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-xs">
                  {(user.name || user.email || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <span>{user.name || user.email}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
